import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import {
  AuthService,
  type QrStatusResponse,
} from "../../shared/services/auth.service";
import { ButtonLarge, ButtonLink } from "../../shared/uikit";

export const ConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qrToken = searchParams.get("qrToken") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<QrStatusResponse | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Fetch QR status on load
  useEffect(() => {
    if (!qrToken) {
      setError("No QR token provided");
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const status = await AuthService.qrStatus(qrToken);
        setDeviceInfo(status);

        // Calculate countdown from expiresAt
        if (status.expiresAt) {
          const expires = new Date(status.expiresAt).getTime();
          const now = new Date().getTime();
          const remaining = Math.max(0, Math.floor((expires - now) / 1000));
          setCountdown(remaining);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to get session status";
        if (message.includes("expired") || message.includes("404")) {
          setError("Session expired or not found");
        } else if (message.includes("confirmed")) {
          setError("This session has already been confirmed");
        } else {
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [qrToken]);

  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const interval = setInterval(() => {
      const newCountdown = countdown - 1;
      setCountdown(newCountdown);
      if (newCountdown <= 0) {
        clearInterval(interval);
        setError("Session expired");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const handleConfirm = async (approved: boolean) => {
    setActionInProgress(true);
    setError(null);

    try {
      await AuthService.qrConfirm(qrToken, approved);
      if (approved) {
        setSuccessMessage("✅ Login confirmed");
        setTimeout(() => navigate(getPath(ConfigRoutes.DEVICES)), 2000);
      } else {
        setSuccessMessage("❌ Login rejected");
        setTimeout(() => navigate(getPath(ConfigRoutes.DEVICES)), 2000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Action failed";
      if (message.includes("expired")) {
        setError("Session expired");
      } else if (message.includes("confirmed")) {
        setError("Session already confirmed");
      } else {
        setError(message);
      }
    } finally {
      setActionInProgress(false);
    }
  };

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>⏳</div>
        <h2>Confirm Login</h2>
        <p style={{ color: "var(--second-color)" }}>
          Fetching session details...
        </p>
      </div>
    );
  }

  // Error state
  if (error && !deviceInfo) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>❌</div>
        <h2>Confirm Login</h2>
        <p style={{ color: "var(--accent-color)" }}>{error}</p>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Success state
  if (successMessage) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>✅</div>
        <h2>{successMessage}</h2>
        <p style={{ color: "var(--second-color)" }}>Redirecting to home...</p>
      </div>
    );
  }

  // Main confirm page
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <div style={{ fontSize: "48px", marginBottom: "1rem" }}>📱</div>
      <h2>Confirm Login</h2>
      <p style={{ color: "var(--second-color)" }}>
        A device is requesting access to your account.
      </p>

      {/* Device info card */}
      <div
        style={{
          margin: "1.5rem auto",
          padding: "1.5rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          maxWidth: "320px",
          background: "var(--second-background)",
          textAlign: "left",
        }}
      >
        <p style={{ margin: "0 0 0.75rem" }}>
          <strong>🖥 Device:</strong> {deviceInfo?.deviceName || "Unknown"}
        </p>
        <p style={{ margin: "0 0 0.75rem" }}>
          <strong>💻 OS:</strong> {deviceInfo?.os || "Unknown"}
        </p>
        <p style={{ margin: "0 0 0.75rem" }}>
          <strong>📱 Platform:</strong> {deviceInfo?.platform || "Unknown"}
        </p>
        {countdown !== null && (
          <p
            style={{
              margin: "0.75rem 0 0",
              color:
                countdown <= 30 ? "var(--color-danger)" : "var(--second-color)",
              fontWeight: 600,
            }}
          >
            ⏱ Expires in: {formatCountdown(countdown)}
          </p>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p
          style={{
            color: "var(--color-danger)",
            fontSize: "14px",
            marginTop: "0.5rem",
          }}
        >
          ❌ {error}
        </p>
      )}

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          justifyContent: "center",
          marginTop: "1.5rem",
        }}
      >
        <ButtonLarge
          title={actionInProgress ? "⏳ Processing..." : "✅ Confirm Login"}
          onClick={() => handleConfirm(true)}
          mode="black"
          isSmall={true}
        />
        <ButtonLarge
          title={actionInProgress ? "⏳ Processing..." : "❌ Reject"}
          onClick={() => handleConfirm(false)}
          mode="black"
          Accent={true}
          isSmall={true}
        />
      </div>

      {/* Back to Devices */}
      <ButtonLink
        title="← Back to Devices"
        onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
      />
    </div>
  );
};
